import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { FACEBOOK_AUTH_URL, GOOGLE_AUTH_URL, toRegistrationSource } from '../../../const';
import { getAuthModalContext } from '../../../store/slices/authSlice';
import { gaEvents } from '../../../utils/analytics';
import { getClearString } from '../../../utils/helper';
import CustomButton, { BUTTON_STYLE_TYPE } from '../../common/Button';
import FacebookBadgeIcon from '../../icons/FacebookBadgeIcon';
import FacebookLoginIconWhite from '../../icons/FacebookLoginIconWhite';
import GoogleLoginIcon from '../../icons/GoogleLoginIcon';
import GoogleLoginIconWhite from '../../icons/GoogleLoginIconWhite';
import styles from './styles.module.scss';

const { REACT_APP_ENVIRONMENT } = process.env;

// HORO-10509: Google/Facebook buttons + "Or continue with" divider, shared
// by EntryStep (signup, stacked/full-width) and SignInStep (compact,
// side-by-side, per the updated Figma) — per comment #163 ("need to add
// social logins to the login screen"), sign-in gets the same OAuth
// options as signup, not just email+password.
//
// `compact` (Sign in / Sign up): divider + a Facebook/Google row — classic
// Facebook blue (#0866FF) button with the white-circle/blue-f badge icon
// (per reference image), white Google button + the real multicolor "G"
// mark. Default (unused since Sign up went full-screen; kept in case a
// future entry point wants the legacy stacked treatment): full-width
// purple buttons.
//
// `dividerFirst` controls the row's position relative to the divider —
// Sign in wants divider-then-row (defaults to `compact`'s value so its
// existing `<OAuthButtons compact />` call sites are unaffected); Sign up's
// Figma puts the row FIRST and the divider after, right before the email
// field, hence the explicit override there.
//
// `desktop` layers on the desktop card's own measurements (confirmed via
// Figma inspector on "Registration screen_general_desktop"): 40px-tall
// buttons and a 16px row gap, vs mobile's 48px/12px — everything else
// (colors, icons, radius) is identical, so this just adds modifier
// classes rather than branching the whole row's markup.
const OAuthButtons = ({ compact = false, dividerFirst = compact, desktop = false }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const clearnedStr = getClearString(pathname);
    const authModalContext = useSelector(getAuthModalContext);
    // HORO-10360: the backend stamps this on the account only if this login creates it.
    const source = `&_source=${toRegistrationSource(authModalContext)}`;

    const redirectUrl = encodeURIComponent(`${window.location.origin}${window.location.pathname}`);
    const pathGoogle =
        REACT_APP_ENVIRONMENT === 'local'
            ? `http://localhost:8080/api/v1/auth/google?_redirect=${clearnedStr}${source}`
            : `${GOOGLE_AUTH_URL}?_redirect=${redirectUrl}${source}`;
    const pathFb =
        REACT_APP_ENVIRONMENT === 'local'
            ? `http://localhost:8080/api/v1/auth/facebook?_redirect=${clearnedStr}${source}`
            : `${FACEBOOK_AUTH_URL}?_redirect=${redirectUrl}${source}`;

    const onGoogleClick = () => {
        gaEvents.setLoginProvider('google');
        window.location.href = pathGoogle;
    };
    const onFbClick = () => {
        gaEvents.setLoginProvider('facebook');
        window.location.href = pathFb;
    };

    if (compact) {
        // HORO-10509: row-first order (Sign up) needs a tighter divider
        // top-margin than divider-first order (Sign in) — see
        // .orDividerGrayRowFirst.
        const dividerClassName = [
            styles.orDividerGray,
            !dividerFirst && styles.orDividerGrayRowFirst,
            desktop && styles.orDividerGrayDesktop
        ]
            .filter(Boolean)
            .join(' ');
        const divider = <p className={dividerClassName}>{t('modal.orContinueWith')}</p>;
        const row = (
            <div className={desktop ? `${styles.oauthRow} ${styles.oauthRowDesktop}` : styles.oauthRow}>
                <button
                    type="button"
                    className={desktop ? `${styles.oauthFacebookBtn} ${styles.oauthBtnDesktop}` : styles.oauthFacebookBtn}
                    onClick={onFbClick}
                >
                    <FacebookBadgeIcon width={20} height={20} />
                    {t('buttons.facebookShort')}
                </button>
                <button
                    type="button"
                    className={desktop ? `${styles.oauthGoogleBtn} ${styles.oauthBtnDesktop}` : styles.oauthGoogleBtn}
                    onClick={onGoogleClick}
                >
                    <GoogleLoginIcon width={18} height={18} />
                    {t('buttons.googleShort')}
                </button>
            </div>
        );
        return dividerFirst ? (
            <>
                {divider}
                {row}
            </>
        ) : (
            <>
                {row}
                {divider}
            </>
        );
    }

    return (
        <>
            <div className={styles.btnWrap}>
                <CustomButton
                    text={t('buttons.google')}
                    onClick={onGoogleClick}
                    LeftIcon={GoogleLoginIconWhite}
                    styleType={BUTTON_STYLE_TYPE.PURPLE}
                />
            </div>
            <div className={styles.btnWrap}>
                <CustomButton
                    text={t('buttons.facebook')}
                    onClick={onFbClick}
                    LeftIcon={FacebookLoginIconWhite}
                    styleType={BUTTON_STYLE_TYPE.PURPLE}
                />
            </div>
            <p className={styles.orDivider}>{t('modal.orContinueWith')}</p>
        </>
    );
};

export default OAuthButtons;
